import React from 'react';
import {
  ArrowRight,
  FileStack,
  FileText,
  Mic,
  Scale,
  ShieldCheck,
} from 'lucide-react';

const DEFAULT_ITEM_META = {
  overview: {
    label: 'Overview',
    description: 'Matter synthesis, hearings, and the current objective.',
    icon: Scale,
  },
  draft: {
    label: 'Draft',
    description: 'Continue drafting, filings, and working copy.',
    icon: FileText,
  },
  files: {
    label: 'Files',
    description: 'Upload and review the record, briefs, and exhibits.',
    icon: FileStack,
  },
  jolly: {
    label: 'Jolly',
    description: 'Capture and review spoken notes inside the matter.',
    icon: Mic,
  },
  review: {
    label: 'Review',
    description: 'Red-team the file and pressure-test the next move.',
    icon: ShieldCheck,
  },
};

export function withQueryParams(href, nextParams = {}) {
  const rawHref = String(href || '').trim();
  if (!rawHref) return '';

  const [pathWithQuery, hash = ''] = rawHref.split('#');
  const [pathname, query = ''] = pathWithQuery.split('?');
  const params = new URLSearchParams(query);

  Object.entries(nextParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      params.delete(key);
      return;
    }
    params.set(key, String(value));
  });

  const queryString = params.toString();
  return `${pathname}${queryString ? `?${queryString}` : ''}${hash ? `#${hash}` : ''}`;
}

function ActionButton({ action, primary = false }) {
  if (!action) return null;
  const Icon = action.icon;
  return (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      data-testid={action.testId}
      className={primary ? 'advoq-btn-primary' : 'app-chat-button'}
      style={primary ? { justifyContent: 'center' } : null}
    >
      {Icon ? <Icon size={14} /> : null}
      {action.label}
    </button>
  );
}

export default function MatterWorkspaceContext({
  matterName = '',
  matterId = '',
  workspaceId = '',
  objective = '',
  items = [],
  activeKey = '',
  compact = false,
  caption = 'Matter workspace',
  primaryAction = null,
  secondaryAction = null,
  testId = 'matter-workspace-context',
}) {
  const title = matterName || matterId || 'Matter';

  return (
    <section
      data-testid={testId}
      className="advoq-card"
      style={{
        padding: compact ? '16px' : '20px',
        borderRadius: compact ? '20px' : '24px',
        border: '1px solid var(--border-subtle)',
        background: compact ? 'var(--bg-surface)' : 'color-mix(in srgb, var(--bg-elevated) 92%, var(--accent-primary) 2%)',
        display: 'grid',
        gap: compact ? '14px' : '18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: compact ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: '14px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, display: 'grid', gap: compact ? '8px' : '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--accent-primary)',
                background: 'rgba(15,118,110,0.08)',
              }}
            >
              {caption}
            </span>
            {workspaceId ? <span className="app-page-chip">Workspace {workspaceId}</span> : null}
            {matterId ? <span className="app-page-chip">{matterId}</span> : null}
          </div>
          <div>
            <h2
              style={{
                margin: 0,
                color: 'var(--text-primary)',
                fontSize: compact ? '20px' : '22px',
                lineHeight: 1.25,
              }}
            >
              {title}
            </h2>
            {objective ? (
              <p
                style={{
                  margin: '6px 0 0',
                  color: 'var(--text-secondary)',
                  fontSize: compact ? '13px' : '14px',
                  lineHeight: 1.65,
                  maxWidth: compact ? '100%' : '720px',
                }}
              >
                {objective}
              </p>
            ) : null}
          </div>
        </div>

        {(primaryAction || secondaryAction) ? (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <ActionButton action={secondaryAction} />
            <ActionButton action={primaryAction} primary />
          </div>
        ) : null}
      </div>

      <div
        data-testid={`${testId}-tabs`}
        style={{
          display: 'grid',
          gridTemplateColumns: compact ? 'repeat(auto-fit, minmax(120px, 1fr))' : 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: compact ? '10px' : '12px',
        }}
      >
        {items.map((item) => {
          const meta = DEFAULT_ITEM_META[item.key] || {};
          const Icon = item.icon || meta.icon || ArrowRight;
          const isActive = activeKey === item.key || Boolean(item.active);
          return (
            <button
              key={item.key}
              type="button"
              data-testid={`${testId}-${item.key}`}
              onClick={item.onClick}
              style={{
                textAlign: 'left',
                borderRadius: compact ? '14px' : '18px',
                border: `1px solid ${isActive ? 'color-mix(in srgb, var(--accent-primary) 38%, var(--border-subtle))' : 'var(--border-subtle)'}`,
                background: isActive ? 'var(--workspace-chip-active-bg)' : 'var(--bg-base)',
                padding: compact ? '12px' : '14px',
                display: 'grid',
                gap: compact ? '6px' : '8px',
                cursor: 'pointer',
              }}
              aria-pressed={isActive}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span
                  style={{
                    width: compact ? '28px' : '32px',
                    height: compact ? '28px' : '32px',
                    borderRadius: compact ? '10px' : '12px',
                    background: 'var(--bg-elevated)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={compact ? 14 : 15} />
                </span>
                {item.badge ? <span className="app-page-chip">{item.badge}</span> : null}
              </div>
              <div style={{ color: isActive ? 'var(--accent-primary)' : 'var(--text-primary)', fontSize: compact ? '13px' : '14px', fontWeight: 700 }}>
                {item.label || meta.label || item.key}
              </div>
              {!compact ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                  {item.description || meta.description}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}
